'use client';
import { TrackedEvents } from '@config/index';
import {
  CHECKOUT_START_DATETIME,
  MIN_PRICES,
  PACKAGE_STORAGE,
  SELECTED_HOTEL_STORAGE,
  SOCIAL_LOGIN_REDIRECT,
} from '@config/storageKeys';
import { useCurrencyContext } from '@contexts/currency-context';
import { trackGa4AddToCart } from '@v2/analytics/ga4';
import { useAppBottomHeaderRef } from '@hooks/use-app-bottom-header';

import routes from '@config/routes';
import { useGetShoppingCartById } from '@hooks/use-get-shopping-cart';
import { usePackagesParams } from '@hooks/use-packages-params';
import { readAdminReplaceParams } from '@hooks/use-admin-replace-params';
import { useBetterMediaQuery } from '@hooks/use-ssr-media-query';
import useStorage from '@hooks/useStorage';
import instance from '@instance';
import { IFormValues } from '@interfaces/buildPackage';
import { PropertyDetails } from '@interfaces/expedia/property';
import { useBuildPackagesStore } from '@store/build-package-store';
import { useCheckoutStepsStore } from '@store/checkout-steps-store';
import { useSearchHotelStore } from '@store/search-hotel-store';
import { useIsClient } from '@uidotdev/usehooks';
import { links, v2Links } from '@utils/navigation/links';
import { updateShoppingCart } from '@v2/action/shopping-cart/shopping-cart-action';
import { ShoppingCartStepEnum } from '@v2/action/shopping-cart/types';
import { Badge, Button, Check } from '@v2/ui';
import { getTotalPriceLabel } from '@v2/views/SearchTickets/components/utils/get-total-price-label';
import { useGetHotels } from '@v2/views/SearchHotel/hooks/use-hotels-list';
import { getCookie, setCookie } from 'cookies-next';
import { useSession } from 'next-auth/react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import pluralize from 'pluralize';
import { useCallback, useState } from 'react';
import { useHotelEstimate } from '@v2/views/SearchHotel/hooks/use-hotel-estimate';

type Props = {
  propertyDetails: PropertyDetails;
};

export function RooomSelectionBottomHeader(props: Props) {
  const bottomHeaderRef = useAppBottomHeaderRef<HTMLDivElement>();
  const storage = useStorage();
  const [currentPackageData] = useState<IFormValues>(
    JSON.parse(storage.getItem(PACKAGE_STORAGE) ?? '{}')
  );

  const { data: sessionData } = useSession();

  const isMd = useBetterMediaQuery('(min-width: 768px)');

  const isClient = useIsClient();
  const { eventLink, packageType, eventId } = usePackagesParams();

  const router = useRouter();
  const { propertyDetails } = props;
  const adminReplace = readAdminReplaceParams(storage);
  const { data: hotels } = useGetHotels();
  const matchingSearchHotel = hotels?.find((h) => h.id === propertyDetails?.id);
  const roomConfigurations = useBuildPackagesStore(
    (state) => state.roomConfigurations
  );
  const { currency } = useCurrencyContext();
  const cartId = useCheckoutStepsStore((state) => state.cartId);

  const roomSelections = useSearchHotelStore((state) => state.roomSelections);

  const {
    quote,
    isRoomFilled,
    selectedHotelAndFlightTotal: totalPrice,
  } = useHotelEstimate({
    kind: 'details',
    property: propertyDetails,
    ready: true,
  });
  const { data: shoppingCartData } = useGetShoppingCartById();

  const nextStep = useCallback(() => {
    const priceValue = quote.amount;
    if (priceValue === null) return;
    const hotel = propertyDetails;
    const setSelectedDataToStorage = async () => {
      if (hotel) {
        const registrationTime = {
          checkIn: currentPackageData?.hotelStartDate,
          checkOut: currentPackageData?.hotelEndDate,
        };
        const guestRating =
          hotel?.guestRating ??
          hotel?.guestRate ??
          matchingSearchHotel?.guestRating ??
          matchingSearchHotel?.guestRate;
        const guestRatingCount =
          hotel?.guestRatingCount ??
          hotel?.guestRateCount ??
          matchingSearchHotel?.guestRatingCount ??
          matchingSearchHotel?.guestRateCount;
        const trustSignals =
          hotel?.trustSignals && hotel.trustSignals.length > 0
            ? hotel.trustSignals
            : matchingSearchHotel?.trustSignals;

        const data = {
          id: hotel.id,
          name: hotel.name,
          location: hotel.address,
          rating: hotel.rating,
          guestRating,
          guestRate: guestRating,
          guestRatingCount,
          guestRateCount: guestRatingCount,
          trustSignals,
          rooms: roomSelections.length,
          registrationTime,
          price: totalPrice,
          roomSelections,
          postalCode: hotel.address.postalCode,
          city: hotel.address.city,
          country: hotel.address.countryCode,
          phone: hotel?.phone,
          fax: hotel?.fax,
          image: hotel?.image,
          fullAddress: hotel?.fullAddress,
        };

        storage.setItem(SELECTED_HOTEL_STORAGE, JSON.stringify(data));

        // Admin item-replacement: the confirm page reads SELECTED_HOTEL_STORAGE (just
        // written above) to do its own price-check before charging/booking — skip GA4,
        // internal tracking, and this cart's persistence entirely, since this cart is a
        // throwaway ADMIN_EDIT snapshot, not a real customer session.
        if (adminReplace.isAdminReplace) return;

        const updatedPackageData = {
          ...currentPackageData,
          selected: { ...currentPackageData?.selected, hotel: true },
        };
        storage.setItem(PACKAGE_STORAGE, JSON.stringify(updatedPackageData));

        const hotelValue = roomSelections.reduce(
          (total, room) => total + Number(room.price || 0),
          0
        );
        const roomQuantity = roomSelections.length || 1;

        trackGa4AddToCart({
          currency,
          value: hotelValue,
          item: {
            item_id: `hotel:${hotel.id}`,
            item_name: hotel.name,
            item_category: 'hotel',
            item_category2:
              packageType === 'packages' ? 'package' : 'tickets_only',
            price: hotelValue / roomQuantity,
            quantity: roomQuantity,
          },
          params: {
            event_id: eventId,
            package_type:
              packageType === 'packages' ? 'package' : 'tickets_only',
          },
        });

        cartId &&
          updateShoppingCart(cartId, {
            selectedHotelDetails: data,
            packageConfigDetails: {
              ...shoppingCartData?.packageConfigDetails,
              ...updatedPackageData,
              totalPrice: priceValue,
            },
            currentStep: ShoppingCartStepEnum.HOTEL_SELECTION,
          });

        const userIp = getCookie('user-ip')?.toString() || 'IP not found';
        const anonymousId =
          getCookie('anonymous-user-id')?.toString() ||
          'anonymous-user-id not found';

        const user = sessionData?.user;

        instance.server(routes.track, {
          body: JSON.stringify({
            userId: user?.id ? Number(user.id) : undefined,
            anonymousId: user ? undefined : anonymousId,
            trackedEvent: TrackedEvents.HOTEL_SELECTED,
            properties: JSON.stringify({
              ip: userIp,
              anonymousId,
              eventName: currentPackageData.eventName,
              eventDate: currentPackageData.eventDate,
              hotelEndDate: currentPackageData.hotelEndDate,
              hotelStartDate: currentPackageData.hotelStartDate,
              hotelId: hotel.id,
              hotelName: hotel.name,
              hotelLocation: hotel.address,
              hotelRating: hotel.rating,
              roomCount: roomSelections.length,
              registrationTime,
              hotelPrice: totalPrice,
              roomSelections,
              hotelPostalCode: hotel.address.postalCode,
              city: hotel.address.city,
              country: hotel.address.countryCode,
              phone: hotel?.phone,
              hotelFax: hotel?.fax,
              hotelAddress: hotel?.fullAddress,
            }),
          }),
          method: 'POST',
        });
      }
    };

    const mp = { ticket: currentPackageData.tickets, hotel: totalPrice };
    storage.setItem(MIN_PRICES, JSON.stringify(mp));

    setSelectedDataToStorage();

    if (adminReplace.isAdminReplace) {
      router.push(v2Links.adminReplaceConfirm);
      return;
    }

    // if(pack)
    const checkoutUrl = `${links.checkout}?eventId=${eventId}&eventLink=${eventLink}&packageType=${packageType}`;

    useCheckoutStepsStore.setState({ hotelSelected: true });

    setCookie(CHECKOUT_START_DATETIME, new Date().getTime());
    storage.setItem(SOCIAL_LOGIN_REDIRECT, checkoutUrl);
    router.push(checkoutUrl);
  }, [
    currentPackageData?.hotelEndDate,
    currentPackageData?.hotelStartDate,
    matchingSearchHotel,
    propertyDetails,
    totalPrice,
    quote.amount,
  ]);

  const bottomTotalPriceLabel = getTotalPriceLabel({
    isFlightIncluded: currentPackageData.selected?.flights,
    isHotelIncluded: true,
    isTicketIncluded:
      packageType === 'packages' || currentPackageData.selected?.tickets,
  });

  if (!isClient || !quote.display) return null;

  return (
    <div
      ref={bottomHeaderRef}
      data-app-bottom-header="true"
      className="app-bottom-header fixed bottom-0 left-0 right-0 z-20 w-full bg-gray-900 pb-[max(12px,env(safe-area-inset-bottom))]"
    >
      <div className="flex flex-col items-center justify-between gap-y-4 px-4 py-5 md:mx-auto md:max-w-[1400px] md:flex-row md:px-20">
        <div>
          <div className="flex items-center justify-between gap-x-3 md:items-start md:justify-start">
            <div>
              <h5 className="text-sm font-bold text-white md:text-lg">
                Estimated {quote.display.perPerson} per person
              </h5>
              {isMd && (
                <span className="text-xs font-medium text-gray-400 md:text-sm">
                  {quote.display.total} in total. {bottomTotalPriceLabel}
                </span>
              )}
            </div>
            <Badge
              size="sm"
              variant="primary"
              className="flex-shrink-0 rounded-full text-sm md:text-base"
            >
              {roomConfigurations.length === roomSelections.length ? (
                <>
                  <Check
                    size="12"
                    pathProps={{ stroke: 'white' }}
                    className="flex-shrink-0"
                  />
                  <span className="ml-2.5 text-xs font-semibold text-white">
                    All {pluralize('room', roomSelections.length)} selected{' '}
                  </span>
                </>
              ) : (
                `${roomConfigurations.length - roomSelections.length} room(s) left`
              )}
            </Badge>
          </div>
          {!isMd && (
            <span className="self-start text-start text-xs font-medium text-gray-400 md:text-sm">
              {quote.display.total} in total. {bottomTotalPriceLabel}
            </span>
          )}
        </div>

        {!isRoomFilled ? (
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center md:w-auto">
            <Link
              className="w-full md:w-auto"
              href={`${links.checkout}?eventId=${eventId}&eventLink=${eventLink}&packageType=${packageType}`}
              onClick={() => {
                useCheckoutStepsStore.setState({ hotelSelected: false });
                setCookie(CHECKOUT_START_DATETIME, new Date().getTime());
                storage.setItem(
                  SOCIAL_LOGIN_REDIRECT,
                  `${links.checkout}?eventId=${eventId}&eventLink=${eventLink}&packageType=${packageType}`
                );
              }}
            >
              <Button
                variant="ghost"
                className="w-full border border-gray-700 bg-transparent text-sm text-white md:w-auto"
              >
                {"I don't need a hotel"}
              </Button>
            </Link>
            <Button
              disabled
              className="w-full cursor-not-allowed bg-gray-800 text-sm text-gray-400 md:w-auto"
            >
              Select a room to continue
            </Button>
          </div>
        ) : (
          <Button
            onClick={nextStep}
            disabled={!isRoomFilled}
            className="w-full md:w-max"
          >
            Continue
          </Button>
        )}
      </div>
    </div>
  );
}
